import type { PaletteMode } from '@mui/material'
import { red } from '@mui/material/colors'

import type {
  BorderPalette,
  ChatPalette,
  ColorScale,
  SimpleSemanticColor,
  StatusPalette,
} from './types'

export { red }

export const brand = {
  50: '#F5F2FB',
  100: '#EAE5F6',
  200: '#D5CBEF',
  300: '#B7A7E2',
  400: '#957FD2',
  500: '#7B5BC2',
  600: '#6A4EA6',
  700: '#5A3E8E',
  800: '#4A3274',
  900: '#3A275B',
} satisfies ColorScale

export const neutral = {
  0: '#FFFFFF',
  50: '#F8F9FB',
  100: '#F1F2F6',
  200: '#E4E6EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
} satisfies ColorScale

export const darkNeutral = {
  0: neutral[900],
  50: neutral[800],
  100: neutral[700],
  200: neutral[600],
  300: neutral[500],
  400: neutral[400],
  500: neutral[300],
  600: neutral[200],
  700: neutral[100],
  800: neutral[50],
  900: neutral[0],
} satisfies ColorScale

export const darkBrand = {
  50: brand[900],
  100: brand[800],
  200: brand[700],
  300: brand[600],
  400: brand[500],
  500: brand[400],
  600: brand[300],
  700: brand[200],
  800: brand[100],
  900: brand[50],
} satisfies ColorScale

export const border: BorderPalette = {
  default: neutral[200],
  soft: neutral[100],
  strong: neutral[300],
}

export const darkBorder: BorderPalette = {
  default: darkNeutral[200],
  soft: darkNeutral[100],
  strong: darkNeutral[300],
}

export const chat: ChatPalette = {
  background: neutral[50],
  backgroundOverlay: 'rgba(255,255,255,0.9)',
  sentBubble: '#E0F7FA',
  receivedBubble: neutral[0],
  commentBubble: neutral[100],
  commentMessageBubble: '#FFF3A3',
  commentMessageText: '#3D3500',
  systemBubble: brand[50],
  replyPreview: neutral[0],
  highlight: 'rgba(25, 118, 210, 0.35)',
}

export const darkChat: ChatPalette = {
  background: '#0B1020',
  backgroundOverlay: 'rgba(11,16,32,0.88)',
  sentBubble: '#163563',
  receivedBubble: darkNeutral[0],
  commentBubble: darkNeutral[100],
  commentMessageBubble: '#E8D16F',
  commentMessageText: '#2F2800',
  systemBubble: darkBrand[50],
  replyPreview: darkNeutral[50],
  highlight: 'rgba(96, 165, 250, 0.35)',
}

export const status: StatusPalette = {
  success: {
    background: '#ECFDF3',
    text: '#027A48',
    border: '#ABEFC6',
  },
  warning: {
    background: '#FFF4E5',
    text: '#B54708',
    border: '#F9C27D',
  },
  error: {
    background: '#FDECEC',
    text: '#B42318',
    border: '#F7B2B2',
  },
  info: {
    background: '#EFF8FF',
    text: '#175CD3',
    border: '#B2DDFF',
  },
}

export const darkStatus: StatusPalette = {
  success: {
    background: '#052E1A',
    text: '#86EFAC',
    border: '#166534',
  },
  warning: {
    background: '#422006',
    text: '#FDBA74',
    border: '#9A3412',
  },
  error: {
    background: '#450A0A',
    text: '#FCA5A5',
    border: '#991B1B',
  },
  info: {
    background: '#082F49',
    text: '#7DD3FC',
    border: '#0369A1',
  },
}

export const whatsapp: SimpleSemanticColor = {
  main: '#25D366',
  contrastText: neutral[0],
}

export const lightPalette = {
  mode: 'light' as const,
  brand,
  neutral,
  border,
  chat,
  status,
  whatsapp,
  red,
  common: {
    black: '#000000',
    white: neutral[0],
  },
  primary: {
    main: brand[600],
    contrastText: neutral[0],
  },
  secondary: {
    main: '#5bdbf8',
    contrastText: '#0A0A0A',
  },
  success: {
    main: status.success.text,
    light: status.success.background,
    dark: '#21734b',
    contrastText: neutral[0],
  },
  warning: {
    main: status.warning.text,
    light: status.warning.background,
    dark: '#8a6d3b',
    contrastText: neutral[0],
  },
  info: {
    main: status.info.text,
    light: status.info.background,
    dark: '#0f4ca8',
    contrastText: neutral[0],
  },
  error: {
    main: red[600],
    light: red[500],
    dark: red[700],
    contrastText: neutral[0],
  },
  background: {
    default: neutral[50],
    paper: neutral[0],
    muted: neutral[100],
  },
  text: {
    primary: neutral[900],
    secondary: neutral[700],
  },
  divider: border.default,
  action: {
    hover: neutral[100],
    selected: 'rgba(0, 183, 224, 0.14)',
  },
}

export const darkPalette = {
  mode: 'dark' as const,
  brand: darkBrand,
  neutral: darkNeutral,
  border: darkBorder,
  chat: darkChat,
  status: darkStatus,
  whatsapp,
  red,
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: {
    main: darkBrand[600],
    contrastText: darkNeutral[0],
  },
  secondary: {
    main: '#67E8F9',
    contrastText: '#082F49',
  },
  success: {
    main: darkStatus.success.text,
    light: darkStatus.success.background,
    dark: '#22C55E',
    contrastText: darkNeutral[0],
  },
  warning: {
    main: darkStatus.warning.text,
    light: darkStatus.warning.background,
    dark: '#F97316',
    contrastText: darkNeutral[0],
  },
  info: {
    main: darkStatus.info.text,
    light: darkStatus.info.background,
    dark: '#38BDF8',
    contrastText: darkNeutral[0],
  },
  error: {
    main: darkStatus.error.text,
    light: darkStatus.error.background,
    dark: '#F87171',
    contrastText: darkNeutral[0],
  },
  background: {
    default: darkNeutral[50],
    paper: darkNeutral[0],
    muted: darkNeutral[100],
  },
  text: {
    primary: darkNeutral[900],
    secondary: darkNeutral[700],
  },
  divider: darkBorder.default,
  action: {
    hover: darkNeutral[100],
    selected: 'rgba(165, 138, 224, 0.22)',
  },
}

export const getPalette = (mode: PaletteMode) =>
  mode === 'dark' ? darkPalette : lightPalette

export const palette = lightPalette
